import React from 'react';
import { useTranslation } from 'react-i18next';

const HREFLANG_LANGUAGES = ['en', 'ja', 'zh-Hans', 'ko', 'es'];

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  ogImageUrl?: string;
}

export function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = '/og-image.svg',
  ogType = 'website',
  canonicalUrl,
  noindex = false,
  ogImageUrl,
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const defaultTitle = t('app.title', 'Date Calculator');
  const defaultDescription = t('app.description', 'Free online date calculator with business day calculations, timezone support, and multiple date operations');
  const defaultKeywords = t('app.keywords', 'date calculator, business days, timezone, week number, weekday, date difference, time duration');

  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const ogPageTitle = ogTitle || pageTitle;
  const ogPageDescription = ogDescription || pageDescription;
  const effectiveOgImage = ogImageUrl || ogImage;

  // Update document title
  React.useEffect(() => {
    document.title = title ? `${pageTitle} - ${defaultTitle}` : pageTitle;
  }, [pageTitle, title, defaultTitle]);

  // Update meta tags
  React.useEffect(() => {
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          (meta as HTMLMetaElement).setAttribute('property', name);
        } else {
          (meta as HTMLMetaElement).setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', pageKeywords);
    updateMetaTag('og:title', ogPageTitle, true);
    updateMetaTag('og:description', ogPageDescription, true);
    updateMetaTag('og:image', effectiveOgImage, true);
    updateMetaTag('og:image:alt', ogPageDescription, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:locale', currentLanguage, true);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', ogPageTitle);
    updateMetaTag('twitter:description', ogPageDescription);
    updateMetaTag('twitter:image', effectiveOgImage);

    // Canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }

    // Noindex
    if (noindex) {
      let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    }

    return () => {
      // Cleanup could be added here if needed
    };
  }, [
    pageDescription,
    pageKeywords,
    ogPageTitle,
    ogPageDescription,
    effectiveOgImage,
    ogType,
    canonicalUrl,
    noindex,
    currentLanguage,
  ]);

  return null;
}

/**
 * Add JSON-LD structured data to the page
 */
export function JSONLD({ data }: { data: object }) {
  React.useEffect(() => {
    let script = document.getElementById('structured-data') as HTMLScriptElement | null;
    
    if (!script) {
      script = document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(data);

    return () => {
      script?.remove();
    };
  }, [data]);

  return null;
}

/**
 * Add hreflang tags for multilingual SEO
 */
export function Hreflang({ baseUrl, path = '' }: { baseUrl: string; path?: string }) {
  React.useEffect(() => {
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;

    HREFLANG_LANGUAGES.forEach((lang) => {
      // Use query parameter for language switching
      const href = `${cleanBaseUrl}${cleanPath}${lang !== 'en' ? `?lang=${lang}` : ''}`;
      const selector = `link[rel="alternate"][hreflang="${lang}"]`;
      let link = document.querySelector(selector) as HTMLLinkElement | null;

      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }

      link.setAttribute('href', href);
    });

    // Add x-default
    const defaultSelector = 'link[rel="alternate"][hreflang="x-default"]';
    let defaultLink = document.querySelector(defaultSelector) as HTMLLinkElement | null;

    if (!defaultLink) {
      defaultLink = document.createElement('link');
      defaultLink.setAttribute('rel', 'alternate');
      defaultLink.setAttribute('hreflang', 'x-default');
      document.head.appendChild(defaultLink);
    }

    defaultLink.setAttribute('href', `${cleanBaseUrl}${cleanPath}`);
  }, [baseUrl, path]);

  return null;
}
